import React, { useState, useRef } from 'react';
import { Upload, Check } from 'lucide-react';
import { StepIndicator } from './UIComponents';
import { api } from '../services/api';
import { useNotification } from './NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';

const UploadRender = () => {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [renderSettings, setRenderSettings] = useState({
    name: '',
    resolution: '1080p',
    duration: 10,
    quality: 'Standard',
    priority: 'Standard'
  });
  const fileInputRef = useRef(null);
  const { addToast } = useNotification();

  // Handle file selection
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  // Handle file change from input
  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.blend')) {
        addToast('Please select a .blend file', 'error');
        return;
      }
      
      // Validate file size (max 500MB)
      const maxSize = 500 * 1024 * 1024; // 500MB
      if (file.size > maxSize) {
        addToast('File size must be less than 500MB', 'error');
        return;
      }
      
      // Update render name based on file name
      setRenderSettings({
        ...renderSettings,
        name: file.name.replace(/\.[^/.]+$/, "") // Remove file extension
      });
      
      try {
        setIsUploading(true);
        setUploadProgress(0);
        console.log('📤 Starting file upload:', file.name);

        const result = await api.uploadBlendFile(file, (percent) => {
          setUploadProgress(percent);
        });

        if (result.success) {
          console.log('✅ File upload successful:', result);

          // Store the file information properly
          setUploadedFile({
            fileName: result.data.filename,
            filePath: result.data.path, // This is the key field for the backend
            fileSize: result.data.size,
            mimetype: result.data.mimetype,
            uploadDate: result.data.uploadDate
          });

          setFileUploaded(true);
          addToast(t('fileUploadedSuccessfully') || 'File uploaded successfully!', 'success');
        } else {
          throw new Error(result.message || 'Upload failed');
        }
      } catch (error) {
        console.error('❌ Upload error:', error);
        addToast(`Upload failed: ${error.message}`, 'error');
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    }
  };

  // Handle field changes in render settings
  const handleSettingsChange = (e) => {
    setRenderSettings({
      ...renderSettings,
      [e.target.name]: e.target.value
    });
  };

  // Submit render job to the backend
  const submitRenderJob = async () => {
    if (!uploadedFile || !uploadedFile.filePath) {
      addToast('No file uploaded or file path missing', 'error');
      return;
    }

    // Validate required fields
    if (!renderSettings.name.trim()) {
      addToast('Please enter a project name', 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      console.log('➕ Creating render job with data:', {
        renderSettings,
        uploadedFile
      });

      // Prepare job data with the correct structure
      const jobData = {
        name: renderSettings.name.trim(),
        blendFilePath: uploadedFile.filePath, // Use the path from upload response
        resolution: renderSettings.resolution,
        duration: parseInt(renderSettings.duration),
        quality: renderSettings.quality,
        priority: renderSettings.priority,
        // Optional metadata
        originalFileName: uploadedFile.fileName,
        fileSize: uploadedFile.fileSize
      };

      console.log('📋 Submitting job data:', jobData);

      const response = await api.createRenderJob(jobData);
      
      if (response.success) {
        addToast(t('renderJobCreatedSuccessfully') || 'Render job created successfully!', 'success');
        
        // Wait a moment then navigate to renders page
        setTimeout(() => {
          // Reset form
          setStep(1);
          setFileUploaded(false);
          setUploadedFile(null);
          setRenderSettings({
            name: '',
            resolution: '1080p',
            duration: 10,
            quality: 'Standard',
            priority: 'Standard'
          });
          
          // Navigate to renders (you might want to use a proper navigation method)
          if (window.location.hash) {
            window.location.hash = '#renders';
          }
        }, 1500);
      } else {
        throw new Error(response.error || 'Failed to create render job');
      }
    } catch (error) {
      console.error('❌ Error creating render job:', error);
      addToast(`Failed to create render job: ${error.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-white">{t('newRender')}</h1>
      <p className="text-gray-400 mt-1">{t('uploadYourBlenderFile')}</p>
      
      <div className="mt-8">
        <div className="flex mb-8">
          <StepIndicator number="1" text={t('uploadFile')} active={step >= 1} completed={step > 1} />
          <StepIndicator number="2" text={t('renderSettings')} active={step >= 2} completed={step > 2} />
          <StepIndicator number="3" text={t('reviewAndSubmit')} active={step >= 3} completed={step > 3} />
        </div>
        
        {step === 1 && (
          <div className="bg-gray-800 rounded-lg shadow p-8">
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept=".blend"
              onChange={handleFileChange}
            />
            <div className={`border-2 border-dashed rounded-lg p-12 flex flex-col items-center justify-center transition-colors
                              ${fileUploaded ? 'border-red-400 bg-gray-700' : 'border-gray-600 hover:border-red-500'}`}>
              {!fileUploaded ? (
                <>
                  <Upload size={48} className={`text-gray-400 mb-4 ${isUploading ? 'animate-bounce' : ''}`} />
                  <h3 className="font-medium text-white mb-1">{t('dragAndDropBlend')}</h3>
                  <p className="text-gray-400 text-sm mb-4">{t('orClickToBrowse')}</p>
                  {isUploading ? (
                    <div className="w-full max-w-xs">
                      <div className="flex items-center justify-center mb-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500 mr-2"></div>
                        <span className="text-white text-sm">{t('uploading')}... {uploadProgress}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-gray-400 text-xs mt-1 text-center">
                        {uploadProgress < 100 ? 'Uploading file to server...' : 'Processing...'}
                      </p>
                    </div>
                  ) : (
                    <button
                      className="bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleFileSelect}
                      disabled={isUploading}
                    >
                      {t('selectFile')}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <Check size={48} className="text-red-500 mb-4" />
                  <h3 className="font-medium text-white mb-1">{uploadedFile.fileName}</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    {Math.round(uploadedFile.fileSize / 1024)} KB · {t('uploadedSuccessfully')}
                  </p>
                  <div className="flex space-x-4">
                    <button 
                      className="border border-gray-600 text-gray-300 px-4 py-2 rounded text-sm hover:bg-gray-700"
                      onClick={() => {
                        setFileUploaded(false);
                        setUploadedFile(null);
                        setRenderSettings({
                          ...renderSettings,
                          name: ''
                        });
                      }}
                    >
                      {t('changeFile')}
                    </button>
                    <button 
                      className="bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700"
                      onClick={() => setStep(2)}
                    >
                      {t('continue')}
                    </button>
                  </div>
                </>
              )}
            </div>
            
            <div className="mt-6 bg-gray-700 border border-gray-600 rounded-lg p-4 text-gray-200 text-sm">
              <p><strong>Tips:</strong> {t('tipsPackTextures')}</p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li>{t('makesure')}</li>
                <li>{t('filesize')}</li>
                <li>{t('supported')}</li>
              </ul>
            </div>
          </div>
        )}
        
        {step === 2 && (
          <div className="bg-gray-800 rounded-lg shadow p-8">
            <h2 className="text-lg font-semibold text-white mb-6">{t('renderSettings')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('projectName')} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={renderSettings.name}
                  onChange={handleSettingsChange}
                  className="block w-full bg-gray-700 border border-gray-600 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder={t('enterProjectName')}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('resolution')}
                </label>
                <select 
                  name="resolution"
                  value={renderSettings.resolution}
                  onChange={handleSettingsChange}
                  className="block w-full bg-gray-700 border border-gray-600 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="720p">720p (1280x720)</option>
                  <option value="1080p">1080p (1920x1080)</option>
                  <option value="1440p">1440p (2560x1440)</option>
                  <option value="4K">4K (3840x2160)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('animationLength')} (seconds)
                </label>
                <input
                  type="number"
                  name="duration"
                  value={renderSettings.duration}
                  onChange={handleSettingsChange}
                  min="1"
                  max="300"
                  className="block w-full bg-gray-700 border border-gray-600 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                <p className="text-xs text-gray-400 mt-1">Maximum: 300 {t('seconds')} (5 {t('minutes')})</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('renderQuality')}
                </label>
                <select 
                  name="quality"
                  value={renderSettings.quality}
                  onChange={handleSettingsChange}
                  className="block w-full bg-gray-700 border border-gray-600 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="Standard">{t('standardPlan')} (128 samples)</option>
                  <option value="High">{t('highQuality')} (256 samples)</option>
                  <option value="Ultra">{t('ultraQuality')} (512 samples)</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t('queuePriority')}
                </label>
                <select 
                  name="priority"
                  value={renderSettings.priority}
                  onChange={handleSettingsChange}
                  className="block w-full bg-gray-700 border border-gray-600 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="Economy">{t('economyQueue')} (Slowest, Free)</option>
                  <option value="Standard">{t('standardQueueOption')} (Normal speed)</option>
                  <option value="Priority">{t('priorityQueue')} (Faster, +$5)</option>
                  <option value="Express">{t('expressQueue')} (Fastest, +$10)</option>
                </select>
              </div>
            </div>
            
            <div className="border-t border-gray-700 mt-8 pt-6 flex justify-between">
              <button 
                className="border border-gray-600 text-gray-300 px-4 py-2 rounded hover:bg-gray-700"
                onClick={() => setStep(1)}
              >
                {t('back')}
              </button>
              <button 
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                onClick={() => setStep(3)}
                disabled={!renderSettings.name.trim()}
              >
                {t('continue')}
              </button>
            </div>
          </div>
        )}
        
        {step === 3 && (
          <div className="bg-gray-800 rounded-lg shadow p-8">
            <h2 className="text-lg font-semibold text-white mb-6">{t('reviewAndSubmit')}</h2>
            
            <div className="border border-gray-700 rounded-lg">
              <div className="border-b border-gray-700 p-4">
                <div className="flex justify-between">
                  <span className="text-gray-400">{t('projectName')}</span>
                  <span className="font-medium text-white">{renderSettings.name}</span>
                </div>
              </div>
              
              <div className="border-b border-gray-700 p-4">
                <div className="flex justify-between">
                  <span className="text-gray-400">File</span>
                  <span className="font-medium text-white">{uploadedFile?.fileName || 'Unknown'}</span>
                </div>
              </div>
              
              <div className="border-b border-gray-700 p-4">
                <div className="flex justify-between">
                  <span className="text-gray-400">{t('resolution')}</span>
                  <span className="font-medium text-white">{renderSettings.resolution}</span>
                </div>
              </div>
              
              <div className="border-b border-gray-700 p-4">
                <div className="flex justify-between">
                  <span className="text-gray-400">{t('animationLength')}</span>
                  <span className="font-medium text-white">{renderSettings.duration} {t('seconds')}</span>
                </div>
              </div>
              
              <div className="border-b border-gray-700 p-4">
                <div className="flex justify-between">
                  <span className="text-gray-400">{t('renderQuality')}</span>
                  <span className="font-medium text-white">{renderSettings.quality}</span>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex justify-between">
                  <span className="text-gray-400">{t('queuePriority')}</span>
                  <span className="font-medium text-white">{renderSettings.priority} Queue</span>
                </div>
              </div>
            </div>
            
            <div className="border-t border-gray-700 mt-8 pt-6">
              {(renderSettings.priority === 'Priority' || renderSettings.priority === 'Express') && (
                <div className="bg-gray-700 p-4 rounded-lg mb-6">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-300">{t('rendercost')}</span>
                    <span className="font-medium text-white">{t('free')}</span>
                  </div>
                  {renderSettings.priority === 'Priority' && (
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-300">{t('priorityQueueAccess')}</span>
                      <span className="font-medium text-white">$5.00</span>
                    </div>
                  )}
                  {renderSettings.priority === 'Express' && (
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-300">{t('expressQueueAccess')}</span>
                      <span className="font-medium text-white">$10.00</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-gray-600 mt-2">
                    <span className="font-medium text-white">{t('total')}</span>
                    <span className="font-bold text-red-500">
                      {renderSettings.priority === 'Priority' ? '$5.00' : 
                       renderSettings.priority === 'Express' ? '$10.00' : 'Free'}
                    </span>
                  </div>
                </div>
              )}
              
              {renderSettings.priority === 'Standard' || renderSettings.priority === 'Economy' ? (
                <div className="bg-green-900/20 border border-green-500/30 p-4 rounded-lg mb-6">
                  <p className="text-green-400 font-medium">✅ {t('freerender')}</p>
                  <p className="text-gray-300 text-sm mt-1">
                    {t('availablerenders')}
                  </p>
                </div>
              ) : null}
              
              <div className="flex justify-between">
                <button 
                  className="border border-gray-600 text-gray-300 px-4 py-2 rounded hover:bg-gray-700"
                  onClick={() => setStep(2)}
                  disabled={isSubmitting}
                >
                  {t('back')}
                </button>
                <button 
                  className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  onClick={submitRenderJob}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('creating')}
                    </>
                  ) : (
                    t('submitRenderJob')
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadRender;